import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogContainer, Dialog, Heading, Divider, Content, ButtonGroup, Button, TextArea } from '@react-spectrum/s2';
import { UserFeedbackSubmission } from '../../types';
const DIMENSIONS: {
    key: keyof UserFeedbackSubmission;
}[] = [
    {
        key: 'relevance'
    },
    {
        key: 'completeness'
    },
    {
        key: 'faithfulness'
    },
    {
        key: 'coherence'
    }
];
interface UserFeedbackModalProps {
    onSubmit: (feedback: UserFeedbackSubmission) => void;
    onClose: () => void;
    existingFeedback?: UserFeedbackSubmission;
}
export const UserFeedbackModal: React.FC<UserFeedbackModalProps> = ({ onSubmit, onClose, existingFeedback })=>{
    const { t } = useTranslation();
    const [scores, setScores] = useState<Record<string, number | null>>({
        relevance: existingFeedback?.relevance.score ?? null,
        completeness: existingFeedback?.completeness.score ?? null,
        faithfulness: existingFeedback?.faithfulness.score ?? null,
        coherence: existingFeedback?.coherence.score ?? null
    });
    const [reasonings, setReasonings] = useState<Record<string, string>>({
        relevance: existingFeedback?.relevance.reasoning ?? '',
        completeness: existingFeedback?.completeness.reasoning ?? '',
        faithfulness: existingFeedback?.faithfulness.reasoning ?? '',
        coherence: existingFeedback?.coherence.reasoning ?? ''
    });
    const allScoresSet = Object.values(scores).every((s)=>s !== null);
    const handleScoreClick = useCallback((dimension: string, score: number)=>{
        setScores((prev)=>({
                ...prev,
                [dimension]: score
            }));
    }, []);
    const handleReasoningChange = useCallback((dimension: string, value: string)=>{
        setReasonings((prev)=>({
                ...prev,
                [dimension]: value
            }));
    }, []);
    const handleSubmit = useCallback(()=>{
        if (!allScoresSet) return;
        const feedback: UserFeedbackSubmission = {
            relevance: {
                score: scores.relevance as number,
                reasoning: reasonings.relevance || ''
            },
            completeness: {
                score: scores.completeness as number,
                reasoning: reasonings.completeness || ''
            },
            faithfulness: {
                score: scores.faithfulness as number,
                reasoning: reasonings.faithfulness || ''
            },
            coherence: {
                score: scores.coherence as number,
                reasoning: reasonings.coherence || ''
            }
        };
        onSubmit(feedback);
    }, [
        allScoresSet,
        scores,
        reasonings,
        onSubmit
    ]);
    return (<DialogContainer onDismiss={onClose}>
      <Dialog>
        <Heading>{t('chat.feedback.heading')}</Heading>
        <Divider/>
        <Content>
          <div className={" sd171 _ta171 Ui171 qi171"}>
            {DIMENSIONS.map((dim)=>{
        const dimensionLabel = t(`chat.feedback.dimensions.${dim.key}.label`);
        const dimensionDescription = t(`chat.feedback.dimensions.${dim.key}.description`);
        return (<div key={dim.key} className="feedback-dimension">
                <div className="feedback-dimension-header">
                  <span className="feedback-dimension-label">
                    {dimensionLabel}
                    <span className="feedback-dimension-required" aria-hidden="true">
                      *
                    </span>
                  </span>
                  <span className="feedback-dimension-desc">{dimensionDescription}</span>
                </div>
                <div className="feedback-stars">
                  {[
            1,
            2,
            3,
            4,
            5
        ].map((star)=>(<button key={star} className={`feedback-star ${scores[dim.key] !== null && star <= (scores[dim.key] as number) ? 'feedback-star--active' : ''}`} onClick={()=>handleScoreClick(dim.key, star)} aria-label={t('chat.feedback.scoreLabel', {
                dimension: dimensionLabel,
                star
            })}>
                      {star}
                    </button>))}
                </div>
                <TextArea aria-label={t('chat.feedback.reasoningLabel', {
            dimension: dimensionLabel
        })} placeholder={t('chat.feedback.reasoningPlaceholder')} value={reasonings[dim.key]} onChange={(value)=>handleReasoningChange(dim.key, value)} styles={" Za171"} UNSAFE_style={{
            minHeight: 64
        }}/>
              </div>);
    })}
          </div>
        </Content>
        <ButtonGroup>
          <Button variant="secondary" onPress={onClose}>
            {t('primitives.cancel')}
          </Button>
          <Button variant="accent" onPress={handleSubmit} isDisabled={!allScoresSet}>
            {t('chat.feedback.submit')}
          </Button>
        </ButtonGroup>
      </Dialog>
    </DialogContainer>);
};
export default UserFeedbackModal;

import "macro-932a5e50c36450bc9a003523127cc4fa94fa35bc5859a4dd902f400f415796a3.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";